import { useCallback, useRef, useState } from 'react';

import { redactGraphqlRequestErrorForTelemetry } from 'components/api/graphqlRequestError';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { captureError } from 'helpers/captureError';

import {
  BuyerTourCreateDocument,
  type BuyerTourCreateMutation,
  type TourAppointmentFieldsFragment,
} from './__generated__/tours.zeus';
import {
  type BookTourFailureReason,
  bookTourFailureReason,
  bookTourFailureReasonFromResultCode,
} from './tourErrors';
import { DEFAULT_TOUR_DURATION_MINUTES } from './useTourAvailability';

export type BookTourInput = {
  listingId: string;
  appointmentDate: string;
  appointmentTime: string;
  durationMinutes?: number;
};

export type BookTourResult =
  | { kind: 'booked'; appointment: TourAppointmentFieldsFragment }
  | { kind: 'failed'; reason: BookTourFailureReason };

const BOOKING_TIMEOUT_MS = 15_000;
// BuyerTourCreate has no idempotency key, so after an ambiguous outcome (client timeout, or a
// transport failure where the request may still have been processed server-side) an immediate
// retry must not re-dispatch. Server-side idempotency is GROWTH-3615.
const BOOKING_RETRY_COOLDOWN_MS = 30_000;

const inputKey = (input: BookTourInput): string =>
  [
    input.listingId,
    input.appointmentDate,
    input.appointmentTime,
    input.durationMinutes ?? DEFAULT_TOUR_DURATION_MINUTES,
  ].join('|');

// Keyed by booking input at module scope: a guard must never return one input's result for
// another, and must survive a remount of the consuming sheet (close/reopen) inside the
// ambiguity window, which hook-instance refs would not.
const inFlightByInput = new Map<string, Promise<BookTourResult>>();
const ambiguousUntilByInput = new Map<string, number>();
const ambiguousResultByInput = new Map<string, BookTourResult>();

const isAmbiguousOutcome = (result: BookTourResult): boolean =>
  result.kind === 'failed' && (result.reason === 'timeout' || result.reason === 'unknown');

export async function bookTour(input: BookTourInput): Promise<BookTourResult> {
  let data: BuyerTourCreateMutation;
  try {
    data = await requestZeusFromBrowser(
      BuyerTourCreateDocument,
      {
        input: {
          listingId: input.listingId,
          appointmentDate: input.appointmentDate,
          appointmentTime: input.appointmentTime,
          durationMinutes: input.durationMinutes ?? DEFAULT_TOUR_DURATION_MINUTES,
        },
      },
      AbortSignal.timeout(BOOKING_TIMEOUT_MS),
    );
  } catch (error) {
    captureError(redactGraphqlRequestErrorForTelemetry(error), 'BuyerTourCreate');
    return { kind: 'failed', reason: bookTourFailureReason(error) };
  }
  const result = data.buyerTourCreate;
  switch (result.__typename) {
    case 'TourAppointment':
      return { kind: 'booked', appointment: result };
    case 'InvalidInputError':
    case 'NotFoundError':
    case 'UnauthorizedError':
      return { kind: 'failed', reason: bookTourFailureReasonFromResultCode(result.code) };
    default:
      return { kind: 'failed', reason: 'unknown' };
  }
}

export function useBookTour() {
  const [isBooking, setIsBooking] = useState(false);
  const [lastResult, setLastResult] = useState<BookTourResult | null>(null);
  // Bumped by reset so a booking that resolves after the sheet closed (and
  // dropped its result) cannot leak a stale outcome into the next session.
  const resetGeneration = useRef(0);
  // Per instance on purpose: the dedup maps are module scope, so deriving this
  // from inFlightByInput.size would let a sibling sheet's booking hold this
  // instance's confirm button disabled.
  const inFlightCount = useRef(0);

  const book = useCallback((input: BookTourInput): Promise<BookTourResult> => {
    const key = inputKey(input);
    const generation = resetGeneration.current;
    const inFlight = inFlightByInput.get(key);
    if (inFlight) {
      return inFlight;
    }
    if (Date.now() < (ambiguousUntilByInput.get(key) ?? 0) && ambiguousResultByInput.has(key)) {
      const cached = ambiguousResultByInput.get(key)!;
      setLastResult(cached);
      return Promise.resolve(cached);
    }
    inFlightCount.current += 1;
    setIsBooking(true);
    const run = bookTour(input)
      .then((result) => {
        if (resetGeneration.current === generation) {
          setLastResult(result);
        }
        if (isAmbiguousOutcome(result)) {
          ambiguousResultByInput.set(key, result);
          ambiguousUntilByInput.set(key, Date.now() + BOOKING_RETRY_COOLDOWN_MS);
        } else {
          ambiguousResultByInput.delete(key);
          ambiguousUntilByInput.delete(key);
        }
        return result;
      })
      .finally(() => {
        if (inFlightByInput.get(key) === run) {
          inFlightByInput.delete(key);
        }
        inFlightCount.current -= 1;
        if (inFlightCount.current === 0) {
          setIsBooking(false);
        }
      });
    inFlightByInput.set(key, run);
    return run;
  }, []);

  const reset = useCallback((): void => {
    resetGeneration.current += 1;
    setLastResult(null);
  }, []);

  return { book, isBooking, lastResult, reset };
}

/** Clears the module-scope guards; test isolation only. */
export function resetBookTourGuardsForTests(): void {
  inFlightByInput.clear();
  ambiguousUntilByInput.clear();
  ambiguousResultByInput.clear();
}
